import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Divider,
    Snackbar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { 
    getFlashBetQuestionTemplates, 
    getFlashBetMatch,
    addFlashBetMatchQuestion,
    updateFlashBetMatchQuestion 
} from '../Api';
import FlashBetQuestionTemplateDto from '../models/dtos/FlashBetQuestionTemplateDto';
import { FlashBetMatchDto, AddMatchQuestionRequest, UpdateMatchQuestionByIdRequest, MatchQuestionDto } from '../models/dtos/FlashBetMatchDto';
import AnswerDto from '../models/dtos/AnswerDto';

interface MatchQuestionModalProps {
    open: boolean;
    matchId: string;
    questionId?: string; // If provided, we're editing; otherwise adding
    matchEnabled?: boolean; // If true, modal will be readonly
    onSave: () => void;
    onClose: () => void;
}

type LocationOption = 'PRE_MATCH' | 'IN_PLAY' | 'POST_MATCH';

const MatchQuestionModal: React.FC<MatchQuestionModalProps> = ({ 
    open, 
    matchId, 
    questionId,
    matchEnabled = false,
    onSave, 
    onClose 
}) => {
    const [formData, setFormData] = useState({
        title: '',
        localizations: {} as { [key: string]: string },
        answers: [] as AnswerDto[],
        correctAnswerId: 0,
        location: 'PRE_MATCH' as LocationOption
    });
    const [localizationEntries, setLocalizationEntries] = useState<Array<{ lang: string; name: string }>>([]);
    const [questionTemplates, setQuestionTemplates] = useState<FlashBetQuestionTemplateDto[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [match, setMatch] = useState<FlashBetMatchDto | null>(null);
    const [existingQuestion, setExistingQuestion] = useState<MatchQuestionDto | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open, matchId, questionId]);

    const loadData = async () => {
        try {
            const [templatesData, matchData] = await Promise.all([
                getFlashBetQuestionTemplates(),
                getFlashBetMatch(matchId)
            ]);
            
            setQuestionTemplates(templatesData);
            setMatch(matchData);

            if (questionId && matchData) {
                // Editing existing question
                const question = matchData.questions.find(q => q.id === questionId);
                if (question) {
                    setExistingQuestion(question);
                    populateFormWithQuestion(question);
                }
            } else {
                // Adding new question - reset form
                resetForm();
            }
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };

    const populateFormWithQuestion = (question: MatchQuestionDto) => {
        setFormData({
            title: question.question.title,
            localizations: question.question.localizations,
            answers: question.question.answers,
            correctAnswerId: question.correctAnswerId,
            location: question.location
        });

        // Set localizations for display
        setLocalizationEntries(Object.entries(question.question.localizations).map(([lang, name]) => ({ lang, name })));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            localizations: {},
            answers: [],
            correctAnswerId: 0,
            location: 'PRE_MATCH'
        });
        setLocalizationEntries([]);
        setSelectedTemplateId('');
    };

    const handleTemplateSelect = (templateId: string) => {
        setSelectedTemplateId(templateId);
        if (!templateId) return;

        const template = questionTemplates.find(t => t.id === templateId);
        if (template) {
            setFormData({
                title: template.title,
                localizations: template.localizations,
                answers: template.answers,
                correctAnswerId: template.answers.length > 0 ? template.answers[0].id : 0,
                location: template.allowedLocations.length > 0 ? template.allowedLocations[0] : 'PRE_MATCH'
            });

            // Set localizations for display
            setLocalizationEntries(Object.entries(template.localizations).map(([lang, name]) => ({ lang, name })));
        }
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear errors when user starts typing
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleAddLocalization = () => {
        setLocalizationEntries([...localizationEntries, { lang: '', name: '' }]);
    };

    const handleRemoveLocalization = (index: number) => {
        const newLocalizations = localizationEntries.filter((_, i) => i !== index);
        setLocalizationEntries(newLocalizations);
        updateLocalizations(newLocalizations);
    };

    const handleLocalizationChange = (index: number, field: 'lang' | 'name', value: string) => {
        const newLocalizations = [...localizationEntries];
        newLocalizations[index][field] = value;
        setLocalizationEntries(newLocalizations);
        updateLocalizations(newLocalizations);
    };

    const updateLocalizations = (localizations: Array<{ lang: string; name: string }>) => {
        const localizationObj = localizations.reduce((acc, loc) => {
            if (loc.lang && loc.name) {
                acc[loc.lang] = loc.name;
            }
            return acc;
        }, {} as { [key: string]: string });

        setFormData(prev => ({
            ...prev,
            localizations: localizationObj
        }));
    };

    const handleAddAnswer = () => {
        const newAnswer: AnswerDto = {
            id: Date.now(), // Temporary ID
            name: '',
            localizations: {}
        };
        setFormData(prev => ({
            ...prev,
            answers: [...prev.answers, newAnswer]
        }));
    };

    const handleRemoveAnswer = (index: number) => {
        const newAnswers = formData.answers.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            answers: newAnswers,
            // Reset correct answer if the removed answer was selected
            correctAnswerId: newAnswers.find(a => a.id === prev.correctAnswerId) ? prev.correctAnswerId : (newAnswers.length > 0 ? newAnswers[0].id : 0)
        }));
    };

    const handleAnswerChange = (index: number, field: 'name', value: string) => {
        const newAnswers = [...formData.answers];
        newAnswers[index][field] = value;
        setFormData(prev => ({
            ...prev,
            answers: newAnswers
        }));
    };

    const addAnswerLocalization = (answerIndex: number) => {
        // Add a placeholder localization with empty language code
        const placeholderLang = ``;
        updateAnswerLocalization(answerIndex, placeholderLang, '');
    };

    const removeAnswerLocalization = (answerIndex: number, lang: string) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.map((answer, i) => {
                if (i === answerIndex) {
                    const newLocalizations = { ...answer.localizations };
                    delete newLocalizations[lang];
                    return { ...answer, localizations: newLocalizations };
                }
                return answer;
            })
        }));
    };

    const updateAnswerLocalization = (answerIndex: number, lang: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            answers: prev.answers.map((answer, i) => {
                if (i === answerIndex) {
                    return {
                        ...answer,
                        localizations: {
                            ...answer.localizations,
                            [lang]: value
                        }
                    };
                }
                return answer;
            })
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Question title is required';
        }

        if (formData.answers.length === 0) {
            newErrors.answers = 'At least one answer is required';
        }

        if (formData.answers.some(a => !a.name.trim())) {
            newErrors.answers = 'All answers must have names';
        }

        if (formData.correctAnswerId === 0 && formData.answers.length > 0) {
            newErrors.correctAnswerId = 'Correct answer must be selected';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            const questionData = {
                title: formData.title,
                localizations: formData.localizations,
                answers: formData.answers.map(answer => ({
                    id: answer.id,
                    name: answer.name,
                    localizations: answer.localizations
                }))
            };

            if (questionId && existingQuestion) {
                // Editing existing question
                const requestData: UpdateMatchQuestionByIdRequest = {
                    question: questionData,
                    correctAnswerId: formData.correctAnswerId,
                    location: formData.location
                };
                await updateFlashBetMatchQuestion(matchId, questionId, requestData);
            } else {
                // Adding new question
                const requestData: AddMatchQuestionRequest = {
                    question: questionData,
                    correctAnswerId: formData.correctAnswerId,
                    location: formData.location
                };
                await addFlashBetMatchQuestion(matchId, requestData);
            }
            
            onSave();
        } catch (error) {
            console.error('Failed to save question:', error);
            setErrorMessage(`Failed to ${questionId ? 'update' : 'add'} question. Please try again.`);
        }
    };

    const handleClose = () => {
        resetForm();
        setErrors({});
        onClose();
    };

    const isEdit = !!questionId;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {isEdit ? 'Edit Question' : 'Add Question'} - {match?.homeTeamDto?.name} vs {match?.awayTeamDto?.name}
            </DialogTitle>
            <DialogContent>
                {matchEnabled && (
                    <Box
                        sx={{
                            backgroundColor: 'warning.light',
                            color: 'warning.contrastText',
                            p: 2,
                            borderRadius: 1,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            ⚠️ Match is enabled - Questions are in read-only mode. Disable the match first to edit questions.
                        </Typography>
                    </Box>
                )}
                <Box component="form" sx={{ mt: 2 }}>
                    {!isEdit && (
                        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Copy from Question Template (Optional)
                            </Typography>
                            <FormControl fullWidth>
                                <InputLabel>Select Template</InputLabel>
                                <Select
                                    value={selectedTemplateId}
                                    onChange={(e) => handleTemplateSelect(e.target.value)}
                                    label="Select Template"
                                    disabled={matchEnabled}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {questionTemplates.map((template) => (
                                        <MenuItem key={template.id} value={template.id}>
                                            {template.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Paper>
                    )}

                    <TextField
                        fullWidth
                        label="Question Title"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        error={!!errors.title}
                        helperText={errors.title}
                        sx={{ mb: 2 }}
                        disabled={matchEnabled}
                    />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                            value={formData.location}
                            onChange={(e) => handleInputChange('location', e.target.value)}
                            label="Location"
                            disabled={matchEnabled}
                        >
                            <MenuItem value="PRE_MATCH">Pre-Match</MenuItem>
                            <MenuItem value="IN_PLAY">In-Play</MenuItem>
                            <MenuItem value="POST_MATCH">Post-Match</MenuItem>
                        </Select>
                    </FormControl>

                    <Divider sx={{ my: 2 }} />

                    {/* Localizations */}
                    <Typography variant="h6" gutterBottom>
                        Question Localizations
                    </Typography>
                    
                    {localizationEntries.map((loc, index) => (
                        <Box key={index} display="flex" gap={1} alignItems="center" sx={{ mb: 1 }}>
                            <TextField
                                label="Language"
                                select
                                value={loc.lang}
                                onChange={(e) => handleLocalizationChange(index, 'lang', e.target.value)}
                                sx={{ minWidth: 120 }}
                                disabled={matchEnabled}
                            >
                                <MenuItem value="">Select language</MenuItem>
                                <MenuItem value="de">German (de)</MenuItem>
                                <MenuItem value="fr">French (fr)</MenuItem>
                                <MenuItem value="it">Italian (it)</MenuItem>
                                <MenuItem value="ro">Romanian (ro)</MenuItem>
                                <MenuItem value="ru">Russian (ru)</MenuItem>
                                <MenuItem value="es">Spanish (es)</MenuItem>
                                <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                <MenuItem value="pl">Polish (pl)</MenuItem>
                                <MenuItem value="tr">Turkish (tr)</MenuItem>
                            </TextField>
                            <TextField
                                fullWidth
                                label="Localized Title"
                                value={loc.name}
                                onChange={(e) => handleLocalizationChange(index, 'name', e.target.value)}
                                disabled={matchEnabled}
                            />
                            <IconButton onClick={() => handleRemoveLocalization(index)} disabled={matchEnabled}>
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    
                    <Button 
                        startIcon={<AddIcon />} 
                        onClick={handleAddLocalization}
                        sx={{ mb: 2 }}
                        disabled={matchEnabled}
                    >
                        Add Localization
                    </Button>

                    <Divider sx={{ my: 2 }} />

                    {/* Answers */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Answers
                        </Typography>
                        <Button 
                            startIcon={<AddIcon />} 
                            onClick={handleAddAnswer}
                            variant="outlined"
                            size="small"
                            disabled={matchEnabled}
                        >
                            Add Answer
                        </Button>
                    </Box>
                    
                    {errors.answers && (
                        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                            {errors.answers}
                        </Typography>
                    )}

                    {formData.answers.map((answer, index) => (
                        <Paper key={answer.id} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                <TextField
                                    fullWidth
                                    label={`Answer ${index + 1} (English)`}
                                    value={answer.name}
                                    onChange={(e) => handleAnswerChange(index, 'name', e.target.value)}
                                    sx={{ mr: 1 }}
                                    disabled={matchEnabled}
                                />
                                <IconButton onClick={() => handleRemoveAnswer(index)} color="error" disabled={matchEnabled}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                            
                            {/* Answer Localizations */}
                            {Object.keys(answer.localizations || {}).length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                        Localizations for Answer {index + 1}:
                                    </Typography>
                                    {Object.entries(answer.localizations || {}).map(([lang, value], langIndex) => (
                                        <Box key={`${index}-${langIndex}`} display="flex" alignItems="center" mb={1}>
                                            <TextField
                                                label="Language"
                                                select
                                                value={lang}
                                                onChange={(e) => {
                                                    // Handle language code change without removing/adding
                                                    const newLang = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        answers: prev.answers.map((ans, i) => {
                                                            if (i === index) {
                                                                const newLocalizations = { ...ans.localizations };
                                                                // Remove old key and add new key
                                                                delete newLocalizations[lang];
                                                                newLocalizations[newLang] = value;
                                                                return { ...ans, localizations: newLocalizations };
                                                            }
                                                            return ans;
                                                        })
                                                    }));
                                                }}
                                                sx={{ mr: 1, width: '120px' }}
                                                size="small"
                                                disabled={matchEnabled}
                                            >
                                                <MenuItem value="">Select language</MenuItem>
                                                <MenuItem value="de">German (de)</MenuItem>
                                                <MenuItem value="fr">French (fr)</MenuItem>
                                                <MenuItem value="it">Italian (it)</MenuItem>
                                                <MenuItem value="ro">Romanian (ro)</MenuItem>
                                                <MenuItem value="ru">Russian (ru)</MenuItem>
                                                <MenuItem value="es">Spanish (es)</MenuItem>
                                                <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                                <MenuItem value="pl">Polish (pl)</MenuItem>
                                                <MenuItem value="tr">Turkish (tr)</MenuItem>
                                            </TextField>
                                            <TextField
                                                fullWidth
                                                label="Localized Answer"
                                                value={value}
                                                onChange={(e) => updateAnswerLocalization(index, lang, e.target.value)}
                                                sx={{ mr: 1 }}
                                                size="small"
                                                disabled={matchEnabled}
                                            />
                                            <IconButton 
                                                color="error" 
                                                onClick={() => removeAnswerLocalization(index, lang)}
                                                size="small"
                                                disabled={matchEnabled}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            
                            <Button
                                size="small"
                                variant="text"
                                startIcon={<AddIcon />}
                                onClick={() => addAnswerLocalization(index)}
                                sx={{ mt: 1 }}
                                disabled={matchEnabled}
                            >
                                Add Localization for Answer {index + 1}
                            </Button>
                        </Paper>
                    ))}

                    <Divider sx={{ my: 2 }} />

                    {/* Correct Answer Selection */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Correct Answer</InputLabel>
                        <Select
                            value={formData.correctAnswerId}
                            onChange={(e) => handleInputChange('correctAnswerId', e.target.value)}
                            label="Correct Answer"
                            error={!!errors.correctAnswerId}
                            disabled={matchEnabled}
                        >
                            {formData.answers.map((answer) => (
                                <MenuItem key={answer.id} value={answer.id}>
                                    {answer.name || `Answer ${formData.answers.indexOf(answer) + 1}`}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.correctAnswerId && (
                            <Typography color="error" variant="caption">
                                {errors.correctAnswerId}
                            </Typography>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={matchEnabled}>
                    {isEdit ? 'Update Question' : 'Add Question'}
                </Button>
            </DialogActions>

            {/* Error Toast */}
            <Snackbar
                open={errorMessage !== null}
                autoHideDuration={4000}
                onClose={() => setErrorMessage(null)}
                message={errorMessage}
            />
        </Dialog>
    );
};

export default MatchQuestionModal;